import React from 'react';
import { Icon } from '@/shared/icons';

/**
 * Um segmento da trilha de navegação.
 */
export interface BreadcrumbItem {
  /** Texto do segmento */
  label: string;
  /** Navegação ao clicar — todo segmento não-final deve tê-lo (F27); ignorado no último */
  onNavigate?: () => void;
  /** data-testid do segmento */
  testid?: string;
}

export interface BreadcrumbProps {
  /** Segmentos da trilha — o ÚLTIMO é a tela atual (não clicável) */
  items: BreadcrumbItem[];
  /**
   * Legenda condensada exibida SÓ no mobile, logo abaixo do segmento final
   * (ex: "CRM · Cliente"). Substitui os segmentos escondidos — NUNCA seta-voltar (F25).
   */
  mobileLegend?: string;
  /** data-testid do <nav> raiz */
  'data-testid'?: string;
}

/**
 * Trilha de navegação padrão das telas de detalhe/formulário (findings F25–F31).
 *
 * - Segmentos não-finais: <button> clicável, muted → card-text no hover, escondidos no mobile
 * - Separador: Icon chevron-right 14px muted, sem opacity extra
 * - Segmento final: 16px semibold, var(--theme-card-text)
 * - Mobile: legenda condensada opcional sob o título (variante C) — nunca seta-voltar
 *
 * @example
 * ```tsx
 * <Breadcrumb
 *   items={[
 *     { label: 'CRM', onNavigate: () => navigate('/crm'), testid: 'crumb-crm' },
 *     { label: 'Clientes', onNavigate: () => navigate('/crm/customers'), testid: 'crumb-list' },
 *     { label: customer.name, testid: 'crumb-current' },
 *   ]}
 *   mobileLegend="CRM · Cliente"
 *   data-testid="customer-breadcrumb"
 * />
 * ```
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  mobileLegend,
  'data-testid': dataTestId,
}) => {
  const current = items[items.length - 1];
  if (!current) return null;

  const previous = items.slice(0, -1);

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 min-w-0 flex-1 text-sm"
      data-testid={dataTestId}
    >
      {previous.map((item, index) => (
        <React.Fragment key={index}>
          {item.onNavigate ? (
            <button
              type="button"
              onClick={item.onNavigate}
              data-testid={item.testid}
              className="hidden sm:inline font-medium truncate transition-colors"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
            >
              {item.label}
            </button>
          ) : (
            /* Fallback estático — uso previsto é sempre com onNavigate (F27) */
            <span
              data-testid={item.testid}
              className="hidden sm:inline font-medium truncate"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {item.label}
            </span>
          )}
          <Icon
            name="chevron-right"
            className="hidden sm:inline w-3.5 h-3.5 flex-none"
            style={{ color: 'var(--theme-muted-text)' }}
          />
        </React.Fragment>
      ))}

      <div className="min-w-0">
        <span
          aria-current="page"
          data-testid={current.testid}
          className="text-base font-semibold truncate block leading-tight"
          style={{ color: 'var(--theme-card-text)' }}
          title={current.label}
        >
          {current.label}
        </span>
        {mobileLegend && (
          <p
            className="sm:hidden text-[11px] truncate leading-none mt-0.5"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            {mobileLegend}
          </p>
        )}
      </div>
    </nav>
  );
};
